// Funções de API para o sistema de Kanban
import { supabase } from "@/integrations/supabase/client";
import type { Tables, TablesUpdate } from "@/integrations/supabase/types";
import { reportApiError } from "@/lib/report-error";
import { toSlug, trackEvent } from "@/lib/telemetry";
import {
  STATUS_LEGADO_PARA_SINAL,
  DIMENSOES_DISPONIBILIDADE_MANUAL,
} from "@/utils/disponibilidadeSignalMap";
import type { FreelancerStatus } from "@/types/freelancer-rating";
import { parseDropReasons } from "@/utils/motivosUtils";
import type {
  KanbanEtapa,
  KanbanCard,
  KanbanMetricas,
  KanbanHistorico,
  VagaRejectionData,
  KanbanObservacao,
  NegociacaoFreelancer,
  NegociacaoAtual,
  StatusNegociacaoFreelancer,
  VagaCardMetricas,
} from "@/types/kanban";

/** De onde veio a adição ao kanban (vagas_kanban_historico.origem, sql/204 e sql/272). */
export enum KanbanAddOrigin {
  ListaVaga = "lista_vaga",
  Lista = "lista",
  SugestoesVaga = "sugestoes_vaga",
  AssistenteCard = "assistente_card",
  AssistenteProposta = "assistente_proposta",
  Kanban = "kanban",
  CopiarVaga = "copiar_vaga",
}

/** Posição no ranking da vaga que estava na tela quando a pessoa adicionou (sql/272). */
export interface KanbanAddRanking {
  posicao: number;
  versao: string;
  calculadoEm: string | null;
}

export interface AddCandidateOptions {
  origem: KanbanAddOrigin;
  ordem?: number;
  usuarioId?: string;
  ranking?: KanbanAddRanking;
}

type HistoricoViewRow = Tables<'view_kanban_historico_completo'>;

function etapaDoHistorico(
  id: string,
  row: HistoricoViewRow,
  nome: string | null,
  cor: string | null,
): KanbanEtapa {
  return {
    id,
    vaga_id: row.vaga_id ?? '',
    nome: nome ?? '',
    ordem: 0,
    cor: cor || '#8B5CF6',
    tipo: 'ativa',
    is_template_default: false,
    created_at: row.timestamp,
    updated_at: row.timestamp,
  };
}

function historicoDaView(row: HistoricoViewRow): KanbanHistorico {
  return {
    id: row.id,
    posicao_id: row.posicao_id,
    etapa_origem_id: row.etapa_origem_id ?? undefined,
    etapa_destino_id: row.etapa_destino_id,
    tipo_movimento: row.tipo_movimento as KanbanHistorico['tipo_movimento'],
    motivo_drop: parseDropReasons(row.motivo_drop),
    observacoes: row.observacoes ?? undefined,
    usuario_id: row.usuario_id ?? undefined,
    timestamp: row.timestamp,
    etapa_origem: row.etapa_origem_id
      ? etapaDoHistorico(row.etapa_origem_id, row, row.etapa_origem_nome, row.etapa_origem_cor)
      : undefined,
    etapa_destino: etapaDoHistorico(row.etapa_destino_id, row, row.etapa_destino_nome, row.etapa_destino_cor),
    usuario: row.usuario_id
      ? {
          full_name: row.usuario_nome ?? undefined,
          username: row.usuario_username ?? undefined,
        }
      : undefined,
  };
}

export function useKanbanApi() {
  
  const getEtapas = async (vagaId: string): Promise<KanbanEtapa[]> => {
    const { data, error } = await supabase
      .from('vagas_kanban_etapas')
      .select('*')
      .eq('vaga_id', vagaId)
      .order('ordem', { ascending: true });
      
    if (error) throw error;
    return (data || []) as KanbanEtapa[];
  };

  const getCards = async (vagaId: string, includeDropInPlace: boolean = false): Promise<KanbanCard[]> => {
    let query = supabase
      .from('view_kanban_completo')
      .select('*')
      .eq('vaga_id', vagaId);
    
    // Filtrar cards com drop in-place, a menos que seja solicitado explicitamente
    if (!includeDropInPlace) {
      query = query.or('is_dropped.is.null,is_dropped.eq.false,drop_tipo.neq.in_place');
    }
    
    query = query
      .order('etapa_ordem', { ascending: true })
      .order('ordem_na_etapa', { ascending: true });
      
    const { data, error } = await query;
      
    if (error) throw error;
    
    // ids dos freelancers p/ os fetches em batch abaixo.
    // Disponibilidade NÃO vem mais daqui: o card lê a fonte única (v_availability_summary)
    // via useDisponibilidadeBatch/DisponibilidadeBadge. Aqui só flags que faltam na view.
    const freelancerIds = [...new Set((data || []).map((row: any) => row.freelancer_id).filter(Boolean))];

    // Buscar flags em batch que não vêm na view: "Fazer Amizade" (falar_com_frila)
    // e is_active (para marcar candidatos inativos no card).
    const amizadeMap = new Map<string, { falar_com_frila: boolean; marked_at: string | null; is_active: boolean }>();
    if (freelancerIds.length > 0) {
      const { data: amizadeData } = await supabase
        .from('freelancers')
        .select('id, falar_com_frila, falar_com_frila_marked_at, is_active')
        .in('id', freelancerIds);
      amizadeData?.forEach((f: any) =>
        amizadeMap.set(f.id, {
          falar_com_frila: !!f.falar_com_frila,
          marked_at: f.falar_com_frila_marked_at,
          is_active: f.is_active !== false,
        })
      );
    }

    // Transformar dados da view para o formato esperado
    // A view retorna dados planos, precisamos estruturar como objetos aninhados
    const cards: KanbanCard[] = (data || [])
      .filter((row: any) => row.posicao_id && row.freelancer_id) // Filtrar linhas vazias (etapas sem candidatos)
      .map((row: any) => {
        return {
          // Dados da posição
          id: row.posicao_id,
          vaga_id: row.vaga_id,
          freelancer_id: row.freelancer_id,
          etapa_atual_id: row.etapa_id,
          ordem_na_etapa: row.ordem_na_etapa ?? 0,
          data_adicao: row.data_adicao || row.created_at,
          is_dropped: row.is_dropped ?? false,
          drop_tipo: row.drop_tipo,
          usuario_adicao: row.usuario_adicao,
          created_at: row.created_at,
          updated_at: row.updated_at,
          
          // Dados do freelancer (mapear campos da view)
          freelancer: {
            id: row.freelancer_id,
            nome: row.freelancer_nome || '',
            email: row.freelancer_email || '',
            areas: Array.isArray(row.freelancer_areas) 
              ? row.freelancer_areas.join(', ') 
              : row.freelancer_areas || '',
            pretensao_salarial: row.freelancer_pretensao_salarial, // String, não número
            avatar_url: row.avatar_url,
            rating: row.rating,
            experiencia_anos: row.experiencia_anos,
            portfolio_url: row.freelancer_portfolio_url || row.portfolio_url,
            linkedin_url: row.freelancer_linkedin_url || row.linkedin_url,
            // Dados de localização
            city: row.freelancer_city,
            state: row.freelancer_state,
            country: row.freelancer_country,
            falar_com_frila: amizadeMap.get(row.freelancer_id)?.falar_com_frila ?? false,
            falar_com_frila_marked_at: amizadeMap.get(row.freelancer_id)?.marked_at ?? null,
            is_active: amizadeMap.get(row.freelancer_id)?.is_active ?? true,
          },
        
        // Dados da etapa (mapear campos da view)
        etapa: {
          id: row.etapa_id,
          vaga_id: row.vaga_id,
          nome: row.etapa_nome || '',
          ordem: row.etapa_ordem ?? 0,
          cor: row.etapa_cor || '#8B5CF6',
          tipo: row.etapa_tipo || 'ativa',
          is_template_default: row.is_template_default ?? false,
          created_at: row.created_at,
          updated_at: row.updated_at,
        },
        
        // Metadados
        total_movimentacoes: row.total_movimentacoes ?? 0,
        ultima_movimentacao: row.ultima_movimentacao,
        };
      });
    
    return cards;
  };

  const getMetricas = async (vagaId: string): Promise<KanbanMetricas> => {
    const { data, error } = await supabase
      .from('view_kanban_metricas')
      .select('*')
      .eq('vaga_id', vagaId)
      .single();
      
    if (error) throw error;
    return {
      ...data,
      total_candidatos: data.total_candidatos ?? 0,
      total_drops: data.total_drops ?? 0,
      total_ativos: data.total_ativos ?? 0,
      candidatos_ultimos_7_dias: data.candidatos_ultimos_7_dias ?? 0,
      candidatos_por_etapa: (data.candidatos_por_etapa ?? {}) as Record<string, number>,
      top_motivos_drop: (data.top_motivos_drop ?? undefined) as KanbanMetricas['top_motivos_drop'],
    };
  };

  const getHistorico = async (posicaoId: string): Promise<KanbanHistorico[]> => {
    const { data, error } = await supabase
      .from('view_kanban_historico_completo')
      .select('*')
      .eq('posicao_id', posicaoId)
      .order('timestamp', { ascending: false });

    if (error) throw error;
    return (data || []).map(historicoDaView);
  };

  // Versão em lote de getHistorico: 1 query pra N posições (evita N+1). Usada
  // quando a UI precisa do histórico de vários cards de uma vez (ex.: motivo de
  // drop de todos os reprovados). Agrupa o resultado por posicao_id.
  const getHistoricoBatch = async (
    posicaoIds: string[]
  ): Promise<Record<string, KanbanHistorico[]>> => {
    const ids = Array.from(new Set(posicaoIds.filter(Boolean)));
    if (ids.length === 0) return {};

    const { data, error } = await supabase
      .from('view_kanban_historico_completo')
      .select('*')
      .in('posicao_id', ids)
      .order('timestamp', { ascending: false });

    if (error) throw error;

    const map: Record<string, KanbanHistorico[]> = {};
    (data || []).forEach((row) => {
      const historico = historicoDaView(row);
      (map[historico.posicao_id] ??= []).push(historico);
    });
    return map;
  };

  const addCandidate = async (
    vagaId: string,
    freelancerId: string,
    etapaId: string,
    { origem, ordem, usuarioId, ranking }: AddCandidateOptions
  ): Promise<void> => {
    // Verificar se o freelancer já está nesta vaga (evita 409 CORS-bloqueado)
    const { data: existente } = await supabase
      .from('vagas_kanban_posicoes')
      .select('id')
      .eq('vaga_id', vagaId)
      .eq('freelancer_id', freelancerId)
      .maybeSingle();

    if (existente) {
      const err: any = new Error('Este freelancer já está nesta vaga.');
      err.code = '23505';
      throw err;
    }

    // Buscar usuário do contexto de auth se não fornecido
    let userId = usuarioId;
    if (!userId) {
      const { data: user } = await supabase.auth.getUser();
      userId = user.user?.id;
    }

    // Inserir a posição no kanban
    const { data: posicaoInserida, error: insertError } = await supabase
      .from('vagas_kanban_posicoes')
      .insert({
        vaga_id: vagaId,
        freelancer_id: freelancerId,
        etapa_atual_id: etapaId,
        ordem_na_etapa: ordem || 0,
        usuario_adicao: userId || null
      })
      .select()
      .single();

    if (insertError) throw insertError;

    // Criar registro no histórico de movimentação
    if (posicaoInserida?.id) {
      const { error: historicoError } = await supabase
        .from('vagas_kanban_historico')
        .insert({
          posicao_id: posicaoInserida.id,
          etapa_destino_id: etapaId,
          tipo_movimento: 'adicao',
          usuario_id: userId || null,
          observacoes: null,
          origem,
          ranking_posicao: ranking?.posicao ?? null,
          ranking_versao: ranking?.versao ?? null,
          ranking_calculado_em: ranking?.calculadoEm ?? null,
        });

      if (historicoError) {
        console.error('Erro ao criar histórico de adição:', historicoError);
        reportApiError(historicoError, { source: "kanban", kind: "historico-adicao" });
        // Não lançar erro aqui para não reverter a inserção da posição
      }
    }
    trackEvent("candidato.adicionar", {
      entidade: { tipo: "freelancer", id: freelancerId },
      contexto: { origem: toSlug(origem), rank: ranking?.posicao, vaga_id: vagaId },
    });
  };

  const moveCard = async (
    posicaoId: string, 
    etapaDestinoId: string, 
    novaOrdem: number,
    observacoes?: string,
    usuarioId?: string
  ): Promise<void> => {
    const { error } = await supabase.rpc('mover_candidato_kanban', {
      p_posicao_id: posicaoId,
      p_etapa_destino_id: etapaDestinoId,
      p_tipo_movimento: 'progressao',
      p_motivo_drop: null,
      p_observacoes: observacoes || null,
      p_usuario_id: usuarioId || null,
      p_nova_ordem: novaOrdem
    });

    if (error) throw error;
    trackEvent("candidato.mover");
  };

  const reorderCard = async (
    posicaoId: string, 
    novaOrdem: number,
    usuarioId?: string
  ): Promise<void> => {
    const { error } = await supabase.rpc('reordenar_card_kanban', {
      p_posicao_id: posicaoId,
      p_nova_ordem: novaOrdem,
      p_usuario_id: usuarioId || null
    });
    
    if (error) throw error;
  };

  const dropCard = async (
    posicaoId: string,
    etapaReprovadoId: string,
    motivos: string[],
    observacoes?: string,
    usuarioId?: string
  ): Promise<void> => {
    // Buscar usuário do contexto de auth se não fornecido
    const { data: user } = await supabase.auth.getUser();
    const userId = usuarioId || user.user?.id;
    
    if (!userId) {
      throw new Error('Usuário não autenticado');
    }

    const { error } = await supabase.rpc('mover_candidato_kanban', {
      p_posicao_id: posicaoId,
      p_etapa_destino_id: etapaReprovadoId,
      p_tipo_movimento: 'drop',
      p_motivo_drop: JSON.stringify(motivos),
      p_observacoes: observacoes || null,
      p_usuario_id: userId,
      p_nova_ordem: null
    });

    if (error) throw error;
    trackEvent("candidato.descartar", { contexto: { motivo: toSlug(motivos[0]) } });
  };

  const rejectVaga = async (
    vagaId: string,
    rejectionData: VagaRejectionData
  ): Promise<void> => {
    const { error } = await supabase
      .from('vagas')
      .update({
        status: 'reprovado',
        ...rejectionData
      })
      .eq('id', vagaId);
      
    if (error) throw error;
  };

  const updateFreelancerDisponibilidade = async (
    freelancerId: string,
    ultimaConsulta: string,
    status: string,
    observacoesStatus?: string,
    usuarioId?: string
  ): Promise<void> => {
    const { data: user } = await supabase.auth.getUser();
    const avaliadorId = usuarioId || user.user?.id;

    if (!avaliadorId) {
      throw new Error('Usuário não autenticado');
    }

    // Fonte única: a marcação humana nasce como SINAL (availability_signals), não mais
    // na coluna freelancer_avaliacoes.status. O badge/timeline/assistente leem a view.
    const nowIso = new Date().toISOString();
    const sinal = STATUS_LEGADO_PARA_SINAL[status as Exclude<FreelancerStatus, 'nao_avaliado'>];

    if (sinal) {
      // 'confirmado_humano' trava acima da IA automática.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from('availability_signals')
        .insert({
          freelancer_id: freelancerId,
          dimension: sinal.dimension,
          value: sinal.value,
          source_type: 'manual_humano',
          author_kind: 'humano',
          author_user_id: avaliadorId,
          confidence: 'alta',
          observed_at: nowIso,
          valid_from: nowIso,
          review_status: 'confirmado_humano',
          observation: observacoesStatus || null,
          metadata: { origem: 'kanban_disponibilidade_modal', ultima_consulta: ultimaConsulta },
        });
      if (error) throw error;
    } else {
      // 'nao_avaliado' (ou status sem mapa): "voltar pra não sei" retrai a marcação
      // humana viva. Soft-delete (padrão da casa: sem GRANT de DELETE) dos sinais
      // manuais de disponibilidade deste freelancer. Antes isso vinha "de graça" pelo
      // trigger M142 ao zerar a coluna; sem a coluna, o front trata explicitamente.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from('availability_signals')
        .update({ deleted_at: nowIso })
        .eq('freelancer_id', freelancerId)
        .eq('source_type', 'manual_humano')
        .in('dimension', DIMENSOES_DISPONIBILIDADE_MANUAL)
        .is('deleted_at', null);
      if (error) throw error;
    }
  };

  const getObservacoes = async (posicaoId: string): Promise<KanbanObservacao[]> => {
    // Tentar usar a view primeiro
    let data, error;
    
    try {
      const result = await supabase
        .from('view_kanban_posicoes_observacoes_completo')
        .select('*')
        .eq('posicao_id', posicaoId)
        .order('created_at', { ascending: false });
      
      data = result.data;
      error = result.error;
      
      // Se a view não existir, usar query manual com join
      if (error) {
        const manualResult = await supabase
          .from('vagas_kanban_posicoes_observacoes')
          .select(`
            *,
            usuario:profiles!user_id(full_name, username, avatar_url)
          `)
          .eq('posicao_id', posicaoId)
          .order('created_at', { ascending: false });
        
        data = manualResult.data;
        error = manualResult.error;
      } else if (data && data.length > 0) {
        // Mapear dados da view para o formato esperado
        return data.map((row: any) => ({
          id: row.id,
          posicao_id: row.posicao_id,
          user_id: row.user_id,
          mensagem: row.mensagem,
          tipo: row.tipo || 'geral',
          is_privada: row.is_privada ?? false,
          created_at: row.created_at,
          updated_at: row.updated_at,
          usuario: row.user_id ? {
            full_name: row.usuario_nome || row.usuario?.full_name,
            username: row.usuario_username || row.usuario?.username,
            avatar_url: row.usuario_avatar_url || row.usuario?.avatar_url,
          } : undefined,
        }));
      }
    } catch (err) {
      // Fallback para query manual
      const manualResult = await supabase
        .from('vagas_kanban_posicoes_observacoes')
        .select(`
          *,
          usuario:profiles!user_id(full_name, username, avatar_url)
        `)
        .eq('posicao_id', posicaoId)
        .order('created_at', { ascending: false });
      
      data = manualResult.data;
      error = manualResult.error;
    }
    
    if (error) throw error;
    
    // Se não for da view, mapear dados do join manual
    if (data && data.length > 0 && data[0].usuario) {
      return data.map((row: any) => ({
        id: row.id,
        posicao_id: row.posicao_id,
        user_id: row.user_id,
        mensagem: row.mensagem,
        tipo: row.tipo || 'geral',
        is_privada: row.is_privada ?? false,
        created_at: row.created_at,
        updated_at: row.updated_at,
        usuario: row.usuario ? {
          full_name: row.usuario.full_name,
          username: row.usuario.username,
          avatar_url: row.usuario.avatar_url,
        } : undefined,
      }));
    }
    
    return data || [];
  };

  // Contagem de observações em lote: 1 query pra N posições (evita N+1). O card
  // do kanban só precisa do NÚMERO de observações pra exibir o badge; o conteúdo
  // completo é carregado sob demanda pelo ObservacoesModal. Antes cada card
  // disparava seu próprio getObservacoes (N+1 em view_kanban_posicoes_observacoes_completo).
  const getObservacoesCountsBatch = async (
    posicaoIds: string[]
  ): Promise<Record<string, number>> => {
    const ids = Array.from(new Set(posicaoIds.filter(Boolean)));
    if (ids.length === 0) return {};

    const { data, error } = await supabase
      .from('view_kanban_posicoes_observacoes_completo')
      .select('posicao_id')
      .in('posicao_id', ids);

    if (error) throw error;

    const counts: Record<string, number> = {};
    (data || []).forEach((row) => {
      const pid = (row as { posicao_id: string }).posicao_id;
      counts[pid] = (counts[pid] ?? 0) + 1;
    });
    return counts;
  };

  const addObservacao = async (
    posicaoId: string,
    mensagem: string,
    userId?: string
  ): Promise<void> => {
    const { data: user } = await supabase.auth.getUser();
    const usuarioId = userId || user.user?.id;
    
    if (!usuarioId) {
      throw new Error('Usuário não autenticado');
    }

    const { error } = await supabase
      .from('vagas_kanban_posicoes_observacoes')
      .insert({
        posicao_id: posicaoId,
        user_id: usuarioId,
        mensagem: mensagem.trim(),
        tipo: 'geral',
        is_privada: false
      });
      
    if (error) throw error;
  };

  const dropCardInPlace = async (
    posicaoId: string,
    motivos: string[],
    observacoes?: string,
    usuarioId?: string
  ): Promise<void> => {
    const { data: user } = await supabase.auth.getUser();
    const userId = usuarioId || user.user?.id;
    
    if (!userId) {
      throw new Error('Usuário não autenticado');
    }

    const { error } = await supabase.rpc('drop_candidato_in_place', {
      p_posicao_id: posicaoId,
      p_motivo_drop: JSON.stringify(motivos),
      p_observacoes: observacoes || null,
      p_usuario_id: userId
    });
    
    if (error) throw error;
  };

  const reactivateCardInPlace = async (
    posicaoId: string,
    observacoes?: string,
    usuarioId?: string
  ): Promise<void> => {
    const { data: user } = await supabase.auth.getUser();
    const userId = usuarioId || user.user?.id;
    
    if (!userId) {
      throw new Error('Usuário não autenticado');
    }

    const { data, error } = await supabase.rpc('reativar_candidato_drop_in_place', {
      p_posicao_id: posicaoId,
      p_observacoes: observacoes || null,
      p_usuario_id: userId
    });
    
    if (error) throw error;
    // A RPC devolve { success, message } em vez de erro quando a posição não
    // está descartada na própria etapa.
    const resultado = (Array.isArray(data) ? data[0] : data) as { success?: boolean; message?: string } | null;
    if (resultado && resultado.success === false) {
      throw new Error(resultado.message || 'Não foi possível reativar o candidato');
    }
  };

  const getCardsDropInPlace = async (vagaId: string): Promise<KanbanCard[]> => {
    const { data, error } = await supabase
      .from('view_kanban_completo')
      .select('*')
      .eq('vaga_id', vagaId)
      .eq('is_dropped', true)
      .eq('drop_tipo', 'in_place')
      .order('updated_at', { ascending: false });
      
    if (error) throw error;
    
    // ids dos freelancers p/ os fetches em batch abaixo (disponibilidade vem da view).
    const freelancerIds = [...new Set((data || []).map((row: any) => row.freelancer_id).filter(Boolean))];

    // Buscar flags em batch que não vêm na view: "Fazer Amizade" (falar_com_frila)
    // e is_active (para marcar candidatos inativos no card).
    const amizadeMap = new Map<string, { falar_com_frila: boolean; marked_at: string | null; is_active: boolean }>();
    if (freelancerIds.length > 0) {
      const { data: amizadeData } = await supabase
        .from('freelancers')
        .select('id, falar_com_frila, falar_com_frila_marked_at, is_active')
        .in('id', freelancerIds);
      amizadeData?.forEach((f: any) =>
        amizadeMap.set(f.id, {
          falar_com_frila: !!f.falar_com_frila,
          marked_at: f.falar_com_frila_marked_at,
          is_active: f.is_active !== false,
        })
      );
    }

    // Transformar dados
    const cards: KanbanCard[] = (data || [])
      .filter((row: any) => row.posicao_id && row.freelancer_id)
      .map((row: any) => {
        return {
          id: row.posicao_id,
          vaga_id: row.vaga_id,
          freelancer_id: row.freelancer_id,
          etapa_atual_id: row.etapa_id,
          ordem_na_etapa: row.ordem_na_etapa ?? 0,
          data_adicao: row.data_adicao || row.created_at,
          is_dropped: row.is_dropped ?? false,
          drop_tipo: row.drop_tipo,
          usuario_adicao: row.usuario_adicao,
          created_at: row.created_at,
          updated_at: row.updated_at,
          
          freelancer: {
            id: row.freelancer_id,
            nome: row.freelancer_nome || '',
            email: row.freelancer_email || '',
            areas: Array.isArray(row.freelancer_areas) 
              ? row.freelancer_areas.join(', ') 
              : row.freelancer_areas || '',
            pretensao_salarial: row.freelancer_pretensao_salarial,
            avatar_url: row.avatar_url,
            rating: row.rating,
            experiencia_anos: row.experiencia_anos,
            portfolio_url: row.freelancer_portfolio_url || row.portfolio_url,
            linkedin_url: row.freelancer_linkedin_url || row.linkedin_url,
            falar_com_frila: amizadeMap.get(row.freelancer_id)?.falar_com_frila ?? false,
            falar_com_frila_marked_at: amizadeMap.get(row.freelancer_id)?.marked_at ?? null,
            is_active: amizadeMap.get(row.freelancer_id)?.is_active ?? true,
          },
        
          etapa: {
            id: row.etapa_id,
            vaga_id: row.vaga_id,
            nome: row.etapa_nome || '',
            ordem: row.etapa_ordem ?? 0,
            cor: row.etapa_cor || '#8B5CF6',
            tipo: row.etapa_tipo || 'ativa',
            is_template_default: row.is_template_default ?? false,
            created_at: row.created_at,
            updated_at: row.updated_at,
          },
          
          total_movimentacoes: row.total_movimentacoes ?? 0,
          ultima_movimentacao: row.ultima_movimentacao,
        };
      });
    
    return cards;
  };

  // ============================================================
  // Negociação por freelancer (migration 101)
  // ============================================================

  const getNegociacoes = async (
    posicaoId: string
  ): Promise<NegociacaoFreelancer[]> => {
    const { data, error } = await supabase
      .from('vagas_kanban_negociacoes')
      .select('*')
      .eq('posicao_id', posicaoId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []) as NegociacaoFreelancer[];
  };

  const addNegociacao = async (
    posicaoId: string,
    payload: {
      status: StatusNegociacaoFreelancer;
      valor?: number | null;
      data_disponibilidade?: string | null;
      observacao?: string | null;
    },
    usuarioId?: string
  ): Promise<NegociacaoFreelancer> => {
    const { data: user } = await supabase.auth.getUser();
    const userId = usuarioId || user.user?.id || null;

    const { data, error } = await supabase
      .from('vagas_kanban_negociacoes')
      .insert({
        posicao_id: posicaoId,
        status: payload.status,
        valor: payload.valor ?? null,
        data_disponibilidade: payload.data_disponibilidade ?? null,
        observacao: payload.observacao ?? null,
        registrado_por: userId,
      })
      .select()
      .single();

    if (error) throw error;
    return data as NegociacaoFreelancer;
  };

  // Mapa posicao_id → negociação atual, para todas as posições de uma vaga
  const getNegociacoesAtuaisByVaga = async (
    vagaId: string
  ): Promise<Record<string, NegociacaoAtual>> => {
    // Join com vagas_kanban_posicoes pra filtrar por vaga_id
    const { data, error } = await supabase
      .from('v_vagas_kanban_negociacao_atual')
      .select('*, vagas_kanban_posicoes!inner(vaga_id)')
      .eq('vagas_kanban_posicoes.vaga_id', vagaId);

    if (error) throw error;

    const map: Record<string, NegociacaoAtual> = {};
    (data || []).forEach((row: any) => {
      map[row.posicao_id] = {
        posicao_id: row.posicao_id,
        negociacao_id: row.negociacao_id,
        status: row.status,
        valor: row.valor,
        data_disponibilidade: row.data_disponibilidade,
        observacao: row.observacao,
        registrado_por: row.registrado_por,
        created_at: row.created_at,
      };
    });
    return map;
  };

  // ============================================================
  // Flags de seleção / envio para a agência (migration 101)
  // ============================================================

  const setSelecionadoParaAgencia = async (
    posicaoId: string,
    value: boolean,
    usuarioId?: string
  ): Promise<void> => {
    const { data: user } = await supabase.auth.getUser();
    const userId = usuarioId || user.user?.id || null;

    const update: TablesUpdate<'vagas_kanban_posicoes'> = {
      selecionado_para_agencia: value,
    };
    if (value) {
      update.selecionado_para_agencia_at = new Date().toISOString();
      update.selecionado_para_agencia_por = userId;
    } else {
      update.selecionado_para_agencia_at = null;
      update.selecionado_para_agencia_por = null;
      // Se desmarcar selecionado, também limpa "enviado"
      update.enviado_para_agencia_at = null;
      update.enviado_para_agencia_por = null;
    }

    const { error } = await supabase
      .from('vagas_kanban_posicoes')
      .update(update)
      .eq('id', posicaoId);

    if (error) throw error;
  };

  const setEnviadoParaAgencia = async (
    posicaoId: string,
    enviado: boolean,
    usuarioId?: string
  ): Promise<void> => {
    const { data: user } = await supabase.auth.getUser();
    const userId = usuarioId || user.user?.id || null;

    const { error } = await supabase
      .from('vagas_kanban_posicoes')
      .update({
        enviado_para_agencia_at: enviado ? new Date().toISOString() : null,
        enviado_para_agencia_por: enviado ? userId : null,
      })
      .eq('id', posicaoId);

    if (error) throw error;
  };

  // Mapa posicao_id → flags, para todas as posições de uma vaga.
  // Útil pra enriquecer cards (a view_kanban_completo não traz as flags novas).
  const getPosicoesFlagsByVaga = async (
    vagaId: string
  ): Promise<
    Record<
      string,
      {
        selecionado_para_agencia: boolean;
        selecionado_para_agencia_at: string | null;
        enviado_para_agencia_at: string | null;
      }
    >
  > => {
    const { data, error } = await supabase
      .from('vagas_kanban_posicoes')
      .select(
        'id, selecionado_para_agencia, selecionado_para_agencia_at, enviado_para_agencia_at'
      )
      .eq('vaga_id', vagaId);

    if (error) throw error;

    const map: Record<string, any> = {};
    (data || []).forEach((row: any) => {
      map[row.id] = {
        selecionado_para_agencia: !!row.selecionado_para_agencia,
        selecionado_para_agencia_at: row.selecionado_para_agencia_at,
        enviado_para_agencia_at: row.enviado_para_agencia_at,
      };
    });
    return map;
  };

  // ============================================================
  // Métricas do card externo (migration 102)
  // ============================================================

  const getCardMetricasBatch = async (
    vagaIds: string[]
  ): Promise<Record<string, VagaCardMetricas>> => {
    if (!vagaIds.length) return {};

    const { data, error } = await supabase
      .from('v_vaga_card_metricas')
      .select('*')
      .in('vaga_id', vagaIds);

    if (error) throw error;

    const map: Record<string, VagaCardMetricas> = {};
    (data || []).forEach((row: any) => {
      map[row.vaga_id] = {
        vaga_id: row.vaga_id,
        qtd_selecionados_kanban: row.qtd_selecionados_kanban ?? 0,
        qtd_shortlist: row.qtd_shortlist ?? 0,
        qtd_aprovados_kanban: row.qtd_aprovados_kanban ?? 0,
        qtd_reprovados_kanban: row.qtd_reprovados_kanban ?? 0,
        qtd_selecionados_agencia: row.qtd_selecionados_agencia ?? 0,
        qtd_enviados_agencia: row.qtd_enviados_agencia ?? 0,
        negociacao_por_status: row.negociacao_por_status || {},
      };
    });
    return map;
  };

  return {
    getEtapas,
    getCards,
    getMetricas,
    getHistorico,
    getHistoricoBatch,
    addCandidate,
    moveCard,
    reorderCard,
    dropCard,
    dropCardInPlace,
    reactivateCardInPlace,
    getCardsDropInPlace,
    rejectVaga,
    updateFreelancerDisponibilidade,
    getObservacoes,
    getObservacoesCountsBatch,
    addObservacao,
    // Negociação
    getNegociacoes,
    addNegociacao,
    getNegociacoesAtuaisByVaga,
    // Flags agência
    setSelecionadoParaAgencia,
    setEnviadoParaAgencia,
    getPosicoesFlagsByVaga,
    // Métricas card externo
    getCardMetricasBatch,
  };
}

